import { confirm, toast } from "@devhop/ui";

/** ຂໍ້ຜິດພາດທີ່ຕ້ອງໃຫ້ UI ສະແດງເອງ (ເຊັ່ນ dialog) — fetcher ຈະບໍ່ toast */
export class ApiUserError extends Error {
  readonly code: string;
  readonly httpStatus: number;

  constructor(code: string, message: string, httpStatus: number) {
    super(message);
    this.name = "ApiUserError";
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  try {
    return await res.clone().json();
  } catch {
    return null;
  }
}

function parseErrorBody(data: unknown): { message?: string; code?: string } {
  if (!data || typeof data !== "object") {
    return {};
  }
  const o = data as Record<string, unknown>;
  const err = o.error;
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    const message = typeof e.message === "string" ? e.message : undefined;
    const code = typeof e.code === "string" ? e.code : undefined;
    return { message, code };
  }
  if (typeof err === "string") {
    return { message: err };
  }
  if (typeof o.message === "string") {
    return { message: o.message };
  }
  return {};
}

export async function handleError(res: Response): Promise<never> {
  const data = await parseJsonSafe(res);
  const status = res.status;
  const parsed = parseErrorBody(data);

  if (status === 409) {
    throw new ApiUserError(
      parsed.code ?? "INSUFFICIENT_STOCK",
      parsed.message ?? "ສຕ໋ອກບໍ່ພໍຄັງ — ກະລຸນາກວດຄືນສຕ໋ອກໃນຄັງ",
      409,
    );
  }

  const message =
    parsed.message ??
    (status === 401
      ? "ບໍ່ໄດ້ຮັບອະນຸຍາດ"
      : status === 403
        ? "ບໍ່ອະນຸຍາດ"
        : status >= 500
          ? "ຜິດພາດທີ່ເຊີບເວີ"
          : "ຄໍາຮ້ອງຂໍລົ້ມເຫຼວ");

  if (status === 401) {
    const ok = await confirm({
      title: "ເຊດຊັນໝົດອາຍຸ",
      description: "ກະລຸນາເຂົ້າລະບົບໃໝ່ເພື່ອດໍາເນີນການຕໍ່.",
      actionText: "ເຂົ້າລະບົບ",
      ActionProps: {
        variant: "default",
      },
    });
    if (ok) window.location.href = "/auth/login";
  } else if (status === 403) {
    console.error(message);
  } else if (status >= 400 && status < 500) {
    toast.error("ຜິດພາດການຮ້ອງຂໍ", { description: String(message) });
  } else {
    toast.error("ຜິດພາດເຊີບເວີ", { description: String(message) });
  }
  throw new Error(message);
}

type JsonRequestOptions = {
  headers?: HeadersInit;
  /** ຍົກ request ຖ້າບໍ່ມີຄຳຕອບພາຍໃນເວລານີ້ (ms) */
  timeoutMs?: number;
};

async function requestJson<TResponse>(
  url: string,
  init: RequestInit,
  options?: JsonRequestOptions,
): Promise<TResponse> {
  const timeoutMs = options?.timeoutMs;
  const controller = timeoutMs ? new AbortController() : null;
  const timeoutId =
    controller && timeoutMs
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

  try {
    const res = await fetch(url, {
      ...init,
      signal: controller?.signal ?? init.signal,
    });
    if (!res.ok) return handleError(res);
    return (await res.json()) as TResponse;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      toast.error("ຄຳຮ້ອງຂໍໃຊ້ເວລານານເກີນໄປ", {
        description: "ກະລຸນາກວດເຄືອຂ່າຍ ຫຼື ລອງໃໝ່.",
      });
      throw new Error("REQUEST_TIMEOUT");
    }
    toast.error("ເຄືອຂ່າຍມີບັນຫາ", {
      description: "ກະລຸນາກວດກາໄຟອິນເຕີເນັດຂອງທ່ານ.",
    });
    throw e;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function jsonHeaders(options?: JsonRequestOptions): Headers {
  const headers = new Headers(options?.headers);
  headers.set("Content-Type", "application/json");
  return headers;
}

export const fetcher = {
  get: async <TResponse>(
    url: string,
    _?: Record<string, unknown>,
    options?: JsonRequestOptions,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(
      url,
      { credentials: "include" },
      options,
    );
  },
  postForm: async <TResponse>(
    url: string,
    body: FormData,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "POST",
      body,
      credentials: "include",
    });
  },
  post: async <TResponse>(
    url: string,
    body?: unknown,
    options?: JsonRequestOptions,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(
      url,
      {
        method: "POST",
        headers: jsonHeaders(options),
        body: JSON.stringify(body ?? {}),
        credentials: "include",
      },
      options,
    );
  },
  put: async <TResponse>(url: string, body?: unknown): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
      credentials: "include",
    });
  },
  putForm: async <TResponse>(
    url: string,
    body: FormData,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PUT",
      body,
      credentials: "include",
    });
  },
  patch: async <TResponse>(url: string, body?: unknown): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
      credentials: "include",
    });
  },
  patchForm: async <TResponse>(
    url: string,
    body: FormData,
  ): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "PATCH",
      body,
      credentials: "include",
    });
  },
  delete: async <TResponse>(url: string): Promise<TResponse> => {
    return requestJson<TResponse>(url, {
      method: "DELETE",
      credentials: "include",
    });
  },
};
